import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/tv/heroSlides";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [total, index]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950">
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {heroSlides.map((slide) => (
          <div key={slide.id} className="relative w-full h-full shrink-0">
            <img
              src={slide.image}
              alt={`${slide.title}剧照`}
              title={`${slide.title} - 绍宋影视`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/30" />
            <div className="relative w-full h-full flex items-end">
              <div className="w-full px-4 md:px-12 pb-16 md:pb-24">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 text-xs rounded-full bg-accent-500 text-primary-950 font-medium">
                      {slide.type === "movie" ? "电影" : "电视剧"}
                    </span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-background-50/15 text-background-50 backdrop-blur">
                      {slide.year}
                    </span>
                    {slide.genres.slice(0, 2).map((g) => (
                      <span key={g} className="hidden sm:inline px-2.5 py-1 text-xs rounded-full bg-background-50/10 text-background-100 backdrop-blur">
                        {g}
                      </span>
                    ))}
                  </div>
                  <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-sm md:text-base text-background-200 leading-7 mb-6 max-w-xl line-clamp-2">
                    {slide.tagline}
                  </p>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/detail/${slide.id}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-lg w-5 h-5 flex items-center justify-center" />
                      查看详情
                    </Link>
                    <Link
                      to={slide.type === "movie" ? "/category/movie" : "/category/tvshow"}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 backdrop-blur text-background-50 text-sm font-medium border border-background-50/25 hover:bg-background-50/20 transition-colors whitespace-nowrap"
                    >
                      更多{slide.type === "movie" ? "电影" : "电视剧"}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/10 backdrop-blur border border-background-50/25 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-background-50/10 backdrop-blur border border-background-50/25 text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}